import { useSyncExternalStore } from "react";

/**
 * ═══════════════════════════════════════════════════════════════
 *  全站外观：配色 / 简繁 / 货币
 * ═══════════════════════════════════════════════════════════════
 *
 * 🔴 **这三样是平台后台定的，不是用户自己挑的。**
 *    老板：「给网站设置几种常见配色，可以切换…这个是平台管理后台来切换」。
 *    所以它们跟登录状态无关 —— 游客、登录页、商品页都按同一套渲染。
 *
 * 🔴 **必须先用本地缓存同步渲染，再拿服务端的值校正。**
 *    等接口回来才上色的话，每次打开都会先闪一下默认的赤陶色再跳成蓝色 ——
 *    那种闪烁比"配色不能改"还难受，而且刷新一次闪一次。
 *    所以：`applyCached()` 在 React 挂载之前同步跑（读 localStorage，0ms），
 *    接口回来了再 `apply()` 一次；两次一致的话用户什么都察觉不到。
 *
 * ⚠️ 缓存可能是**上一次部署**留下的旧值（后台改过了）。这没关系：
 *    校正只晚一个请求，而且配色不影响任何操作的正确性。
 *    货币和简繁同理 —— 它们都只改显示。
 */

export type ThemeKey = "terracotta" | "ocean" | "forest" | "graphite" | "plum";
export type ScriptKey = "hans" | "hant";
export type CurrencyKey = "CNY" | "TWD";

/**
 * 首页「按你家的空间挑」那一格。
 *
 * 🔴 **`key` 是全站共用的词表**：商品上打的 `spaces`、案例上的 `space`、
 *    商品列表的筛选，比的都是它。所以这份数据只有一个来源（后台配置），
 *    页面上一律 `useSpaces()` 取，**不要再在组件里写一份**。
 *    （原先六个文件各写各的，其中网页版商品列表写成了中文 key，
 *    而商品上存的是 `living` —— 那个筛选点了永远是空的。）
 */
export interface SpaceItem {
  key: string;
  label: string;
  /** 网页版那一格底下的一行小字；手机版放不下，不显示。 */
  hint: string;
  /** 图标 key，见 `components/space-icons.data.ts`。 */
  icon: string;
}

export interface SiteConfig {
  name: string;
  icp: string;
  contactPhone: string;
  /** 对外网址，分享链接用它拼。空串 = 用当前域名。 */
  baseUrl: string;
  theme: ThemeKey;
  script: ScriptKey;
  currency: CurrencyKey;
  /**
   * 登录页那一屏（网页版左边那块品牌区）：小标题 · 大标题 · 商品图。
   * 后台「页面管理 → 登录页管理」里配。
   * ⚠️ 它们要在**未登录**时就能拿到，所以走这条不鉴权的 `/api/site-config`。
   */
  loginEyebrow: string;
  loginHeadline: string;
  loginShowcase: "sales" | "manual" | "off";
  loginShowcaseProductIds: number[];
  /**
   * 首页那一排空间入口。后台「页面管理 → 首页空间」里配。
   * ⚠️ 出参里已经滤掉停用的那几格，前台直接铺就行。
   */
  spaces: SpaceItem[];
}

const DEFAULTS: SiteConfig = {
  name: "装修商城平台",
  icp: "",
  contactPhone: "",
  baseUrl: "",
  theme: "terracotta",
  script: "hans",
  currency: "CNY",
  loginEyebrow: "装修建材 · 一站购齐",
  loginHeadline: "装一套房要的建材\n在这儿一次配齐",
  loginShowcase: "sales",
  loginShowcaseProductIds: [],
  /**
   * ⚠️ 这份默认值要跟服务端 `settings/service.ts` 的 `DEFAULTS.spaces` 对上 ——
   *    它是**接口还没回来之前**（首屏、离线、接口挂了）铺出来的那一份。
   *    对不上的话首页会先闪一排别的东西再跳。
   */
  spaces: [
    { key: "living", label: "客厅", hint: "地板 · 灯具 · 墙面", icon: "sofa" },
    { key: "bedroom", label: "卧室", hint: "地板 · 衣柜 · 照明", icon: "bed" },
    { key: "kitchen", label: "厨房", hint: "橱柜 · 台面 · 五金", icon: "kitchen" },
    { key: "bathroom", label: "卫浴", hint: "瓷砖 · 洁具 · 花洒", icon: "shower" },
    { key: "balcony", label: "阳台", hint: "防水 · 晾晒 · 地砖", icon: "balcony" },
  ],
};

const KEY = "zx.site.config";

/** 货币只换符号，**绝不折算金额** —— 见 `appearanceSettingsSchema` 上的红字。 */
export const CURRENCY_SYMBOL: Record<CurrencyKey, string> = {
  CNY: "¥",
  TWD: "NT$",
};

export const THEME_LABEL: Record<ThemeKey, string> = {
  terracotta: "赤陶",
  ocean: "海蓝",
  forest: "松绿",
  graphite: "石墨",
  plum: "梅红",
};

/**
 * 色卡上给后台看的主色（跟 `styles.css` 里的 `--brand-600` 一致）。
 * ⚠️ 这里写死一份是有意的：后台要在**没切换之前**就看到每套长什么样，
 *    而 CSS 变量只能读到当前生效的那一套。改配色时两处要一起改。
 */
export const THEME_SWATCH: Record<ThemeKey, string> = {
  terracotta: "oklch(0.55 0.13 52)",
  ocean: "oklch(0.55 0.15 252)",
  forest: "oklch(0.52 0.12 158)",
  graphite: "oklch(0.44 0.02 270)",
  plum: "oklch(0.52 0.16 355)",
};

let current: SiteConfig = DEFAULTS;
const listeners = new Set<() => void>();

function read(): SiteConfig {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULTS;
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<SiteConfig>) };
  } catch {
    /** ⚠️ 私密窗口 / 存储被禁 / 存的是上个版本的坏数据 —— 一律当没有。 */
    return DEFAULTS;
  }
}

/**
 * 把配置落到 DOM 上。
 * ⚠️ 配色走 `data-theme`（CSS 变量覆盖），简繁走 `lang` ——
 *    `lang` 会影响浏览器选字体和断行，不设的话繁体字在某些字体下会用简体字形。
 */
function paint(cfg: SiteConfig) {
  const el = document.documentElement;
  /** 默认那套写在 `:root`，不打属性就是它 —— 打上 terracotta 也不会错，但没必要。 */
  if (cfg.theme === "terracotta") el.removeAttribute("data-theme");
  else el.setAttribute("data-theme", cfg.theme);
  el.lang = cfg.script === "hant" ? "zh-Hant" : "zh-Hans";
}

/**
 * 🔴 **在 React 挂载之前同步调用一次**（`main.tsx` 的最上面）。
 *    读的是 localStorage，没有网络等待，所以不会闪。
 */
export function applyCachedSiteConfig() {
  current = read();
  paint(current);
}

/** 接口回来之后校正。值没变就什么都不做 —— 别白白触发一轮重渲染。 */
export function applySiteConfig(cfg: SiteConfig) {
  const next = { ...DEFAULTS, ...cfg };
  if (JSON.stringify(next) === JSON.stringify(current)) return;
  current = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* 存不下就算了，下次还是走默认，不影响功能 */
  }
  paint(next);
  for (const fn of listeners) fn();
}

/**
 * 组件里读当前配置。
 * ⚠️ 用 `useSyncExternalStore` 而不是 React state：配置存在模块级变量里，
 *    后台改完接口回来时要让**所有**用到它的组件重渲染（价格符号、繁体转换）。
 */
export function useSiteConfig(): SiteConfig {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
    () => DEFAULTS,
  );
}

/**
 * 页面上取那一排空间。
 *
 * 🔴 **全站只能从这里取。** 商品上打的 `spaces`、案例上的 `space`、
 *    商品列表的筛选、后台打标签的那几个按钮 —— 比的都是同一份 `key`。
 *    各写各的就会走岔：网页版商品列表曾经自己写了一份中文 key
 *    （`space: "客厅"`），而商品上存的是 `living`，那个筛选点了永远是空的。
 *
 * ⚠️ 后台停用的那几格服务端已经滤掉了，这里拿到的就是该显示的。
 */
export function useSpaces(): SpaceItem[] {
  return useSiteConfig().spaces;
}

/** 非组件里也要用（比如格式化函数）—— 直接读当前值。 */
export function siteConfig(): SiteConfig {
  return current;
}

/**
 * 分享出去的链接用哪个域名。
 *
 * 🔴 **不能直接用 `window.location.origin`。** 商家在开发机 / 内网 IP /
 *    `127.0.0.1` 上打开后台，复制出来的专属链接就是
 *    `http://127.0.0.1:5183/i/XXXX` —— 发给客户的手机，那个地址指向的是
 *    **客户自己的设备**，必然打不开；而商家自己点又是好的（他就在那台机器上），
 *    所以这种故障极难被发现。
 *
 * ⚠️ 后台「配置中心 → 站点」里的**对外网址**没填时才退回当前域名
 *    （本机开发方便）。上线必须填。
 */
export function shareOrigin(): string {
  const base = siteConfig().baseUrl?.trim();
  /** ⚠️ 结尾的斜杠要去掉，否则拼出来是 `https://x.com//i/CODE`。 */
  return base ? base.replace(/\/+$/, "") : window.location.origin;
}
